import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import InputField from "../../components/common/InputField";
import Section from "../../components/common/Section";
import SelectField from "../../components/common/SelectField";

import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

import { getLoanById, saveDisbursement } from "../../api/api";
import { handleLeadRedirect } from "../../utils/leadRedirect";

// ================= UTILS =================
const onlyDigits = (val: string) => val.replace(/\D/g, "");
const limitDigits = (val: string, limit = 12) => val.slice(0, limit);

const formatNumber = (val: any) => {
  if (!val && val !== 0) return "";
  return Number(val).toLocaleString("en-IN");
};

const DisbursementForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const location = useLocation();

  const isView = location.pathname.includes("/view/");
  // ================= LOAD =================
  const fetchData = async () => {
    try {
      const res = await getLoanById(id as string);

      const loan = res.data?.data;
      const lead = loan?.leadId;

      if (lead?.status !== "approved" && lead?.status !== "disbursed") {
        handleLeadRedirect(lead, navigate);
        return;
      }

      const disb = loan?.disbursement || {};

      setForm({
        loanAmount: loan.netAmount || "",

        rtoCharges: disb.rtoCharges || "",
        insuranceCharges: disb.insuranceCharges || "",
        otherCharges: disb.otherCharges || "",

        netDisbursement: disb.netDisbursement || "",

        disbursementDate: disb.disbursementDate
          ? disb.disbursementDate.split("T")[0]
          : "",

        firstPayment: disb.firstPayment || "",
        secondPayment: disb.secondPayment || "",

        holdAmount: disb.holdAmount || "",

        rcSubmitted: disb?.pdd?.rcSubmitted ?? false,
        insuranceSubmitted: disb?.pdd?.insuranceSubmitted ?? false,

        pddStatus: disb?.pdd?.status ?? "pending",
      });
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err?.message || "Failed to load disbursement",

          type: "error",
        }),
      );

      navigate("/disbursement");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ================= CHANGE =================
  const handleChange = (key: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [key]: value }));
    setErrors((prev: any) => ({ ...prev, [key]: "" }));
  };

  // ================= VALIDATION =================
  const validateForm = () => {
    const err: any = {};

    if (!form.rtoCharges) err.rtoCharges = "Enter RTO charges";
    if (!form.insuranceCharges)
      err.insuranceCharges = "Enter insurance charges";

    if (!form.firstPayment) err.firstPayment = "Enter bank payment";

    return err;
  };

  // ================= NET CALC =================
  useEffect(() => {
    if (isView) return;

    const base = Number(form.loanAmount || 0);
    const rto = Number(form.rtoCharges || 0);
    const insurance = Number(form.insuranceCharges || 0);
    const other = Number(form.otherCharges || 0);

    const net = base - (rto + insurance + other);

    setForm((prev: any) => ({
      ...prev,
      netDisbursement: net > 0 ? net : 0,
    }));
  }, [
    form.loanAmount,
    form.rtoCharges,
    form.insuranceCharges,
    form.otherCharges,
    isView,
  ]);

  // ================= HOLD CALC =================
  useEffect(() => {
    if (isView) return;
    const net = Number(form.netDisbursement || 0);
    const first = Number(form.firstPayment || 0);
    const second = Number(form.secondPayment || 0);

    const hold = net - (first + second);

    setForm((prev: any) => ({
      ...prev,
      holdAmount: hold > 0 ? hold : 0,
    }));
  }, [form.netDisbursement, form.firstPayment, form.secondPayment, isView]);
  // ================= SUBMIT =================
  const handleSubmit = async () => {
    const validationErrors = isView ? {} : validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);

      await saveDisbursement(id as string, form);

      dispatch(
        showSnackbar({
          message: "Disbursement saved successfully",
          type: "success",
        }),
      );

      navigate("/disbursement");
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err?.message || err?.msg || "Failed to save disbursement",

          type: "error",
        }),
      );
    }
  };

  const handleNumber = (key: string, value: string) => {
    let val = onlyDigits(value);
    val = limitDigits(val);
    handleChange(key, val);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 3, borderRadius: 4 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 2 }}>
          {isView ? "View Disbursement" : "Disbursement Details"}
        </Typography>

        {/* BANK */}
        <Section title="Bank Disbursement" />

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <InputField
            label="Base Amount"
            value={formatNumber(form.loanAmount)}
            disabled
          />

          <InputField
            label="RTO Charges"
            type="tel"
            value={form.rtoCharges}
            onChange={(e: any) => handleNumber("rtoCharges", e.target.value)}
            error={!!errors.rtoCharges}
            helperText={errors.rtoCharges || formatNumber(form.rtoCharges)}
            disabled={isView}
          />

          <InputField
            label="Insurance Charges"
            type="tel"
            value={form.insuranceCharges}
            onChange={(e: any) =>
              handleNumber("insuranceCharges", e.target.value)
            }
            error={!!errors.insuranceCharges}
            helperText={
              errors.insuranceCharges || formatNumber(form.insuranceCharges)
            }
            disabled={isView}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <InputField
            label="Other Charges"
            type="tel"
            value={form.otherCharges}
            onChange={(e: any) => handleNumber("otherCharges", e.target.value)}
            helperText={formatNumber(form.otherCharges)}
            disabled={isView}
          />

          <InputField
            label="Net Disbursement"
            value={formatNumber(form.netDisbursement)}
            disabled
          />

          <InputField
            type="date"
            label="Disbursement Date"
            value={form.disbursementDate}
            onChange={(e: any) =>
              handleChange("disbursementDate", e.target.value)
            }
            disabled={isView}
          />
        </Box>

        {/* PAYMENT */}
        <Section title="Payment Flow" />

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <InputField
            label="1st Payment (Bank)"
            type="tel"
            value={form.firstPayment}
            onChange={(e: any) => handleNumber("firstPayment", e.target.value)}
            error={!!errors.firstPayment}
            helperText={errors.firstPayment || formatNumber(form.firstPayment)}
            disabled={isView}
          />

          <InputField
            label="2nd Payment (Customer)"
            type="tel"
            value={form.secondPayment}
            onChange={(e: any) => handleNumber("secondPayment", e.target.value)}
            helperText={formatNumber(form.secondPayment)}
            disabled={isView}
          />

          <InputField
            label="Hold Amount"
            value={formatNumber(form.holdAmount)}
            disabled
          />
        </Box>

        {/* PDD */}
        <Section
          title={
            isView ? "Update PDD Status" : "PDD (Post Disbursement Documents)"
          }
        />

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <SelectField
              name="status"
              label="Status"
              value={form.pddStatus}
              onChange={(e: any) => handleChange("pddStatus", e.target.value)}
              options={[
                { label: "Pending", value: "pending" },
                { label: "Approved", value: "approved" },
              ]}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!form.rcSubmitted}
                onChange={(e) => handleChange("rcSubmitted", e.target.checked)}
              />
            }
            label="RC Submitted"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={!!form.insuranceSubmitted}
                onChange={(e) =>
                  handleChange("insuranceSubmitted", e.target.checked)
                }
              />
            }
            label="Insurance Submitted"
          />
          <Box sx={{ flex: 1 }} />
        </Box>

        {/* BUTTON */}
        {/* BUTTONS */}
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 3,
            }}
            onClick={() => navigate("/disbursement")}
          >
            Cancel
          </Button>
            <Button
              variant="contained"
              disabled={loading}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                px: 3,
                background:
                  "linear-gradient(135deg, var(--primary), var(--primary-light))",
              }}
              onClick={handleSubmit}
            >
              {loading
                ? "Processing..."
                : isView
                  ? "Update PDD"
                  : "Save Disbursement"}
            </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default DisbursementForm;
